import { TwilioIdentity } from '@televet/shared-types/twilio';
import { useMemo } from 'react';
import {
  ChannelType,
  ChannelViewChannelFragment,
  ChannelViewChannelMemberFragment,
  ChannelViewChannelMessageFragment,
  MessageType,
  useGetAssigneeChannelMembersQuery,
} from 'shared/types/graphql';

interface IUseLastRealMessageReadInput {
  channelData?: ChannelViewChannelFragment | null;
  currentUserId?: string;
  messages: ChannelViewChannelMessageFragment[] | undefined;
}

interface IUseLastRealMessageReadOutput {
  lastEligibleMessageReadByMember: Record<number, Array<ChannelViewChannelMemberFragment>>;
  lastEligibleMessageIndex: number;
}

const hasClinicPetParent = (member: unknown): member is { clinicPetParent: { id: string } } => {
  return typeof member === 'object' && member !== null && 'clinicPetParent' in member;
};

const findVisibleMessage = (
  messages: ChannelViewChannelMessageFragment[],
  minIndex = Infinity,
): ChannelViewChannelMessageFragment | undefined => {
  return messages.find(
    (message) =>
      message.index <= minIndex &&
      message.messageType !== MessageType.Note &&
      message.author?.twilioIdentity !== TwilioIdentity.System,
  );
};

const useLastMessage = ({
  channelData,
  currentUserId,
  messages,
}: IUseLastRealMessageReadInput): IUseLastRealMessageReadOutput => {
  const reversedMessages = useMemo(() => messages?.slice().reverse(), [messages]);

  // Assignee channel members are currently only needed for Team channels
  const { data: assigneeChannelMembersData } = useGetAssigneeChannelMembersQuery({
    skip: channelData?.channelType !== ChannelType.Team || !channelData?.id,
    variables: { channelId: channelData?.id ?? '', assigneeUserIds: channelData?.assignees.map(({ id }) => id) ?? [] },
  });

  const assigneeChannelMembers = assigneeChannelMembersData?.findManyChannelMember;

  const lastEligibleMessageReadByMember = useMemo(() => {
    const lastEligibleMessageReadByMember: Record<number, Array<ChannelViewChannelMemberFragment>> = {};

    if (!channelData?.channelMembers || !reversedMessages?.length) {
      return lastEligibleMessageReadByMember;
    }

    const relevantChannelMembers = [...channelData.channelMembers, ...(assigneeChannelMembers ?? [])].filter(
      (member) => {
        const isClinicPetParent = hasClinicPetParent(member);

        const isAssignedClinicUser = assigneeChannelMembers?.some(({ id }) => id === member.id);

        return (isClinicPetParent || isAssignedClinicUser) && member.user?.id !== currentUserId;
      },
    );

    for (const member of relevantChannelMembers) {
      if (member.lastConsumedMessageIndex == null) continue;
      const lastEligibleMessageReadByMemberIndex = findVisibleMessage(
        reversedMessages,
        member.lastConsumedMessageIndex,
      )?.index;
      if (lastEligibleMessageReadByMemberIndex != null) {
        if (lastEligibleMessageReadByMemberIndex in lastEligibleMessageReadByMember) {
          lastEligibleMessageReadByMember[lastEligibleMessageReadByMemberIndex].push(member);
        } else {
          lastEligibleMessageReadByMember[lastEligibleMessageReadByMemberIndex] = [member];
        }
      }
    }

    return lastEligibleMessageReadByMember;
  }, [channelData, currentUserId, reversedMessages, assigneeChannelMembers]);

  const lastEligibleMessageIndex = useMemo(() => {
    if (!reversedMessages) return -1;
    return findVisibleMessage(reversedMessages)?.index ?? -1;
  }, [reversedMessages]);

  return {
    lastEligibleMessageReadByMember: lastEligibleMessageReadByMember,
    lastEligibleMessageIndex,
  };
};

export default useLastMessage;
